/**
 * Solitaire Game Persistence Service
 *
 * Auto-saves game state to local storage after each move.
 * Follows SudokuGamePersistence pattern.
 */

import { SolitaireGameState } from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedSolitaireGame {
  gameId: string;
  gameState: SolitaireGameState;
  metadata: {
    drawMode: 1 | 3;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class SolitaireGamePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'solitaire_saved_games';
  private readonly AUTO_SAVE_KEY = 'solitaire_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  /** Auto-save the current game state after each move. */
  autoSave(
    gameState: SolitaireGameState,
    metadata: SavedSolitaireGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedSolitaireGame = {
        gameId: 'auto_save',
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save solitaire game:', error);
      return false;
    }
  }

  /** Load the auto-saved game state. */
  loadAutoSave(): SavedSolitaireGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load solitaire auto-save:', error);
      return null;
    }
  }

  /** Clear the auto-saved game state. */
  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear solitaire auto-save:', error);
      return false;
    }
  }

  /** Check if an auto-save exists. */
  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }

  /** Save a game with a specific ID. */
  saveGame(
    gameId: string,
    gameState: SolitaireGameState,
    metadata: SavedSolitaireGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedSolitaireGame = {
        gameId,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };

      const games = this.loadAllGames();
      const existingIndex = games.findIndex((g) => g.gameId === gameId);

      if (existingIndex >= 0) {
        games[existingIndex] = savedGame;
      } else {
        games.push(savedGame);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));
      return true;
    } catch (error) {
      console.error('Failed to save solitaire game:', error);
      return false;
    }
  }

  /** Load all saved games. */
  loadAllGames(): SavedSolitaireGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load solitaire games:', error);
      return [];
    }
  }

  /** Delete a saved game by ID. */
  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filtered = games.filter((g) => g.gameId !== gameId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Failed to delete solitaire game:', error);
      return false;
    }
  }

  /** Clear all saved games. */
  clearAllGames(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear all solitaire games:', error);
      return false;
    }
  }

  /** Sync saved games from cloud storage. */
  async syncFromCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for solitaire games');
      return false;
    } catch (error) {
      console.error('Failed to sync solitaire games from cloud:', error);
      return false;
    }
  }

  /** Sync saved games to cloud storage. */
  async syncToCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for solitaire games');
      return true;
    } catch (error) {
      console.error('Failed to sync solitaire games to cloud:', error);
      return false;
    }
  }
}
